import { Modal, Spin } from 'antd';
import moment from 'moment';
import 'moment/locale/es';
import React, { useState } from 'react';
import { StarFilled } from '@ant-design/icons';
import { useAuth } from '../../../../../store/AuthContext/AuthContext';
import { useFetch } from '../../../../../hooks/useFetch/useFetch';
import SentimentBubbleCloud from './SentimentBubbleCloud';
import { DrawerPanelTitle, modalPanelStyles } from '../../../common/Drawers/DrawerPanelTitle';
import { SentimentBar } from './SentimentBar';
import { SENTIMENT_ICONS } from '../Reviews/Label';
import { getColor as getStarColor } from '../../../../../common/utils/ColorStar';

const PERIOD_LABELS: Record<string, string> = {
	ALL: 'Desde el Inicio',
	CURRENT_MONTH: 'Mes Actual',
	PREVIOUS_MONTH: 'Mes Anterior',
	LAST_7DAYS: 'Últimos 7 días',
	LAST_30DAYS: 'Últimos 30 días',
	LAST_3MONTHS: 'Últimos 3 meses',
	LAST_12MONTHS: 'Últimos 12 meses',
};

const formatFilterPeriodLabel = (filterPeriod: string) => {
	const value = (filterPeriod || '').replace(/^&?filterPeriod=/, '');
	if (!value) {
		return null;
	}
	if (PERIOD_LABELS[value]) {
		return PERIOD_LABELS[value];
	}
	if (value.startsWith('CUSTOM_RANGE')) {
		const dates = value.replace('CUSTOM_RANGE', '').split(',');
		if (dates.length === 2) {
			const start = moment(dates[0], 'DD-MM-YYYY', true);
			const end = moment(dates[1], 'DD-MM-YYYY', true);
			if (start.isValid() && end.isValid()) {
				return `${start.format('DD/MM/YYYY')} – ${end.format('DD/MM/YYYY')}`;
			}
			return `${dates[0]} – ${dates[1]}`;
		}
	}
	return value;
};

const getRankingColor = (score: number) => {
	const clamped = Math.max(0, Math.min(100, score));
	const hue = (clamped / 100) * 120;
	return `hsl(${hue}, 85%, 34%)`;
};

const getRankingDominant = (score: number): 'POSITIVE' | 'NEUTRAL' | 'NEGATIVE' => {
	if (score >= 67) return 'POSITIVE';
	if (score >= 34) return 'NEUTRAL';
	return 'NEGATIVE';
};

const COMMENT_PREVIEW_LENGTH = 140;

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const HIGHLIGHT_BG = '#fef9c3';
const HIGHLIGHT_COLOR = '#854d0e';

const highlightLabelsInText = (text: string, labels: string[] = []) => {
	if (!text) {
		return <i style={{ color: '#94a3b8' }}>Sin texto</i>;
	}

	const uniqueLabels = Array.from(
		new Set(
			labels
				.map((label) => label?.trim())
				.filter((label): label is string => !!label)
				.sort((a, b) => b.length - a.length)
		)
	);

	if (!uniqueLabels.length) {
		return text;
	}

	const regex = new RegExp(`(${uniqueLabels.map(escapeRegExp).join('|')})`, 'gi');
	const parts = text.split(regex);
	const labelSet = new Set(uniqueLabels.map((label) => label.toLowerCase()));

	return parts.map((part, index) => {
		if (labelSet.has(part.toLowerCase())) {
			return (
				<mark
					key={`${part}-${index}`}
					style={{
						background: HIGHLIGHT_BG,
						color: HIGHLIGHT_COLOR,
						fontWeight: 600,
						padding: '0 2px',
						borderRadius: 3,
					}}
				>
					{part}
				</mark>
			);
		}
		return <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>;
	});
};

const ExpandableComment = ({
	text,
	highlightLabels = [],
}: {
	text: string;
	highlightLabels?: string[];
}) => {
	const [expanded, setExpanded] = useState(false);
	const isLong = text.length > COMMENT_PREVIEW_LENGTH;
	const displayText = !text
		? ''
		: expanded || !isLong
			? text
			: `${text.slice(0, COMMENT_PREVIEW_LENGTH)}…`;

	return (
		<>
			<div style={{
				fontSize: 13,
				fontWeight: 500,
				color: '#334155',
				lineHeight: 1.55,
				whiteSpace: 'pre-wrap',
			}}>
				{highlightLabelsInText(displayText, highlightLabels)}
			</div>
			{isLong && (
				<button
					type="button"
					onClick={() => setExpanded((prev) => !prev)}
					style={{
						marginTop: 4,
						padding: 0,
						border: 'none',
						background: 'transparent',
						color: '#2f6fb5',
						fontSize: 12,
						fontWeight: 500,
						cursor: 'pointer',
					}}
				>
					{expanded ? 'Ver menos' : 'Ver más'}
				</button>
			)}
		</>
	);
};

const ExampleReviewCard = ({
	review,
	highlightLabels = [],
}: {
	review: any;
	highlightLabels?: string[];
}) => {
	const commentText = review.comment?.originalText || review.comment?.text || '';
	const starRating = review.starRating || 0;
	const starColor = getStarColor(starRating) || '#64748b';

	return (
		<div style={{
			padding: '10px 12px',
			background: '#fff',
			border: '1px solid #e2e8f0',
			borderRadius: 8,
			marginTop: 0,
		}}>
			<div style={{
				display: 'flex',
				alignItems: 'flex-start',
				justifyContent: 'space-between',
				gap: 10,
			}}>
				<div style={{ flex: 1, minWidth: 0 }}>
					<ExpandableComment
						text={commentText}
						highlightLabels={highlightLabels}
					/>
				</div>
				<span style={{
					display: 'inline-flex',
					alignItems: 'center',
					gap: 3,
					color: '#64748b',
					fontSize: 12,
					fontWeight: 500,
					flexShrink: 0,
					marginTop: 2,
				}}>
					<StarFilled style={{ fontSize: 12, color: starColor }} />
					{(Math.round(10 * starRating) / 10).toFixed(1)}
				</span>
			</div>
			{review.reviewerDisplayName && (
				<div style={{
					marginTop: 8,
					textAlign: 'right',
					fontSize: 12,
					fontWeight: 400,
					color: '#94a3b8',
				}}>
					{review.reviewerDisplayName}
				</div>
			)}
		</div>
	);
};

const GroupLabelItem = ({ group }: { group: any }) => {
	const ranking = typeof group.ranking === 'number' ? group.ranking : null;
	const rankingColor = ranking != null ? getRankingColor(ranking) : '#64748b';
	const groupLabelsList = Array.from(
		new Set(
			[
				...(Array.isArray(group.labels) ? group.labels : []),
				group.clusterName,
			].filter((label): label is string => !!label?.trim())
		)
	);
	const reviews = group.reviews || [];

	return (
		<div style={{
			marginBottom: 14,
			padding: '10px 10px',
			borderRadius: 8,
			background: '#f1f5f9',
			border: '1px solid #cbd5e1',
			display: 'flex',
			flexDirection: 'column',
			gap: 8,
		}}>
			<div style={{
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'space-between',
				gap: 8,
			}}>
				<div style={{
					display: 'inline-block',
					maxWidth: '100%',
					padding: '3px 8px',
					borderRadius: 6,
					background: '#fff',
					border: '1px solid #c4b5fd',
					fontSize: 12,
					fontWeight: 500,
					color: '#5b21b6',
					lineHeight: 1.3,
				}}>
					{group.clusterName}
				</div>
				{ranking != null && (
					<div style={{
						display: 'inline-flex',
						alignItems: 'center',
						gap: 4,
						padding: '2px 7px',
						borderRadius: 6,
						flexShrink: 0,
						background: `color-mix(in srgb, ${rankingColor} 10%, white)`,
						border: `1px solid color-mix(in srgb, ${rankingColor} 45%, white)`,
					}}>
						<span style={{
							display: 'inline-flex',
							alignItems: 'center',
							fontSize: 13,
							lineHeight: 1,
							color: rankingColor,
						}}>
							{React.cloneElement(SENTIMENT_ICONS[getRankingDominant(ranking)], {
								style: { padding: 0, color: rankingColor, fontSize: 13 },
							})}
						</span>
						<span style={{
							fontSize: 12,
							fontWeight: 500,
							color: rankingColor,
							lineHeight: 1.2,
							whiteSpace: 'nowrap',
						}}>
							{Math.round(ranking)}%
						</span>
					</div>
				)}
			</div>

			{!!groupLabelsList.length && (
				<div style={{
					fontSize: 11,
					fontWeight: 400,
					color: '#64748b',
					lineHeight: 1.4,
					paddingLeft: 2,
				}}>
					Incluye las etiquetas:{' '}
					{groupLabelsList.map((label, index) => (
						<span key={label}>
							{index > 0 ? ', ' : ''}
							<b>"{label}"</b>
						</span>
					))}
				</div>
			)}

			{reviews.map((review: any, index: number) => (
				<ExampleReviewCard
					key={`${group.clusterName}-${review.reviewerDisplayName || index}-${index}`}
					review={review}
					highlightLabels={groupLabelsList}
				/>
			))}
		</div>
	);
};

export const LocalLabelsModal = (props: {
	locationId: string;
	location: any;
	category?: any;
	filterCategory: string;
	filterSentiment: string;
	filterLabeled?: string;
	filterPeriod: string;
	onFinish: any;
}) => {

	const [labelsData, setlabelsData] = useState();
	const [groupLabels, setGroupLabels] = useState<any[]>([]);
	const { projectGroup } = useAuth();

	const { isLoading } = useFetch(
		`${process.env.REACT_APP_API}/projects/${projectGroup.project}/${projectGroup.group}/comments/${props.locationId}?${props.filterCategory}${props.filterSentiment}${props.filterLabeled || ''}${props.filterPeriod}`
	  	, {},[]
		,(data) => {
			setlabelsData(data.labels);
			setGroupLabels(Array.isArray(data.groupLabels) ? data.groupLabels : []);
		}
    );

	const category = props.category;
	const totalLabels = category
		? (category.numberOfPositiveLabels || 0)
			+ (category.numberOfNeutralLabels || 0)
			+ (category.numberOfNegativeLabels || 0)
		: 0;
	const opiniones = category?.numberOfReviews
		?? category?.totalUniqueReviews
		?? category?.numberOfOpinions
		?? props.location?.totalUniqueReviews
		?? 0;
	const periodLabel = formatFilterPeriodLabel(props.filterPeriod);
	const hasGroupLabels = groupLabels.length > 0;

	return (
		<Modal
			open={true}
			onCancel={props.onFinish}
			width={'75%'}
			footer={null}
			{...modalPanelStyles}
			bodyStyle={{
				...(modalPanelStyles.bodyStyle || {}),
				paddingTop: 12,
			}}
			title={
				<DrawerPanelTitle
					title="Nube de Etiquetas"
					location={props.location}
				/>
			}
		>
			{category && (
				<div style={{
					display: 'flex',
					flexDirection: 'column',
					gap: 10,
					marginTop: -4,
					marginBottom: 16,
					padding: '14px 16px',
					borderRadius: 10,
					background: '#fff',
					border: '1px solid rgba(148, 163, 184, 0.28)',
				}}>
					<div style={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'flex-start',
						gap: 16,
						flexWrap: 'wrap',
					}}>
						<div style={{ minWidth: 0, flex: 1 }}>
							<div style={{
								fontSize: 11,
								fontWeight: 500,
								color: '#64748b',
								lineHeight: 1.2,
								marginBottom: 8,
							}}>
								Categoría
							</div>
							<div style={{
								display: 'inline-block',
								padding: '8px 14px',
								borderRadius: 20,
								background: '#eef5ff',
								border: '1px solid #1b6ef5',
								fontWeight: 700,
								fontSize: 16,
								color: '#1b6ef5',
								lineHeight: 1.3,
							}}>
								{category.category}
							</div>
						</div>
						{periodLabel && (
							<div style={{ textAlign: 'right' }}>
								<div style={{
									fontSize: 11,
									fontWeight: 500,
									color: '#64748b',
									lineHeight: 1.2,
									marginBottom: 8,
								}}>
									Periodo
								</div>
								<div style={{
									fontWeight: 600,
									fontSize: 13,
									color: '#334155',
									lineHeight: 1.3,
								}}>
									{periodLabel}
								</div>
							</div>
						)}
					</div>
					<SentimentBar
						width="50%"
						height={7.3}
						positive={category.percentPositives || 0}
						neutral={category.percentNeutral || 0}
						negative={category.percentNegatives || 0}
					/>
					<small style={{ color: '#94a3b8', lineHeight: 1.3 }}>
						{totalLabels} etiqueta{totalLabels === 1 ? '' : 's'} extraída{totalLabels === 1 ? '' : 's'} de {opiniones} opini{opiniones === 1 ? 'ón' : 'ones'}
					</small>
				</div>
			)}

			<div style={{
				display: 'flex',
				flexDirection: 'column',
				gap: 14,
				padding: '14px 16px 16px',
				borderRadius: 12,
				background: 'linear-gradient(180deg, #ede9fe 0%, #f5f3ff 38%, #ffffff 100%)',
				border: '1px solid #a78bfa',
				minHeight: 520,
			}}>
				<div style={{
					display: 'flex',
					alignItems: 'center',
					gap: 8,
					flexShrink: 0,
				}}>
					<span style={{
						display: 'inline-flex',
						alignItems: 'center',
						justifyContent: 'center',
						width: 28,
						height: 28,
						borderRadius: 8,
						background: '#7c3aed',
						color: '#ffffff',
						flexShrink: 0,
					}}>
						<span className="material-symbols-outlined" style={{ fontSize: 16, color: '#ffffff' }}>auto_awesome</span>
					</span>
					<div>
						<div style={{
							fontSize: 14,
							fontWeight: 700,
							color: '#0f172a',
							lineHeight: 1.3,
						}}>
							Resumen generado con IA
						</div>
						<div style={{
							fontSize: 11,
							fontWeight: 500,
							color: '#94a3b8',
							lineHeight: 1.3,
							marginTop: 2,
						}}>
							Nube de etiquetas y grupos de sentimiento
						</div>
					</div>
				</div>

				<div style={{ height: 420, minHeight: 420, position: 'relative' }}>
					{isLoading && (
						<div style={{
							position: 'absolute',
							inset: 0,
							zIndex: 2,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							background: 'rgba(255,255,255,0.55)',
							borderRadius: 8,
						}}>
							<Spin />
						</div>
					)}
					<div style={{
						display: 'flex',
						gap: 14,
						alignItems: 'stretch',
						height: '100%',
						width: '100%',
					}}>
						<div style={{
							flex: 1,
							minWidth: 0,
							height: '100%',
							padding: '12px 14px',
							background: '#fff',
							border: '1px solid rgba(148, 163, 184, 0.22)',
							overflow: 'hidden',
							display: 'flex',
							flexDirection: 'column',
							boxSizing: 'border-box',
						}}>
							<div style={{
								fontSize: 11,
								fontWeight: 600,
								color: '#5b21b6',
								letterSpacing: '0.03em',
								textTransform: 'uppercase',
								marginBottom: 10,
								flexShrink: 0,
							}}>
								Etiquetas
							</div>
							<div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
								{labelsData && <SentimentBubbleCloud data={labelsData} />}
							</div>
						</div>

						{hasGroupLabels && (
							<div style={{
								flex: 1,
								minWidth: 0,
								height: '100%',
								padding: '12px 14px',
								background: '#fff',
								border: '1px solid rgba(148, 163, 184, 0.22)',
								borderLeft: '3px solid #6d28d9',
								display: 'flex',
								flexDirection: 'column',
								boxSizing: 'border-box',
								overflow: 'hidden',
							}}>
								<div style={{
									fontSize: 11,
									fontWeight: 600,
									color: '#5b21b6',
									letterSpacing: '0.03em',
									textTransform: 'uppercase',
									marginBottom: 10,
									flexShrink: 0,
								}}>
									Etiquetas Agrupadas
								</div>
								<div style={{
									overflowY: 'auto',
									minHeight: 0,
									flex: 1,
									paddingRight: 4,
								}}>
									{groupLabels.map((group: any, index: number) => (
										<GroupLabelItem
											key={`${group.clusterName || 'group'}-${index}`}
											group={group}
										/>
									))}
								</div>
							</div>
						)}
					</div>
				</div>
			</div>
		</Modal>
	)
}
